import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { PlanService } from '../../Modules/Plans/Services/plan.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { FileDownloadService } from '../../Modules/Shared/Services/file-download.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { EXPORT_FILENAME_PREFIX, EXPORT_STARTED_MESSAGE } from '../../Consts/export.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { OrganizationFilters } from '../../Modules/Organizations/Interfaces/organization-filters.interface';
import { OrganizationSummary } from '../../Models/backoffice/organization-summary.model';
import { PlanSummary } from '../../Models/backoffice/plan-summary.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { OrganizationListResponse } from '../../Interfaces/api/backoffice/organization.interface';
import { PlanListResponse } from '../../Interfaces/api/backoffice/plan.interface';

@Injectable()
export class OrganizationsFacade {
  private readonly _organizationService = inject(OrganizationService);
  private readonly _planService = inject(PlanService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _fileDownload = inject(FileDownloadService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _filters = signal<OrganizationFilters>({
    search: '',
    status: '',
    plan_id: '',
    tenant_health_status: '',
  });

  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isExporting = signal<boolean>(false);

  private readonly _organizations = httpResource<LaravelDefaultResponse<OrganizationListResponse>>(
    () => this._organizationService.listRequest(this._filters(), this._page(), this._pageSize()),
  );

  private readonly _plans = httpResource<LaravelDefaultResponse<PlanListResponse>>(() =>
    this._planService.listRequest(),
  );

  readonly planOptions = computed<PlanSummary[]>(() =>
    this._planService.toPlans(this._resourceState.valueOf(this._plans)),
  );

  readonly filters = computed<OrganizationFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isExporting = computed<boolean>(() => this._isExporting());

  readonly organizations = computed<OrganizationSummary[]>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizations)).items,
  );

  readonly total = computed<number>(
    () => this._organizationService.toPagedResult(this._resourceState.valueOf(this._organizations)).total,
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._organizations.status()),
  );

  readonly isBusy = computed<boolean>(() =>
    this._resourceState.isBusy(this._organizations.status()),
  );

  readonly isEmpty = computed<boolean>(
    () => this.state() === 'ready' && this.organizations().length === 0,
  );

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  applyFilters(filters: OrganizationFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({ search: '', status: '', plan_id: '', tenant_health_status: '' });
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._organizations.reload();
  }

  exportCsv(): void {
    this._isExporting.set(true);

    this._organizationService
      .exportCsv(this._filters())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (blob) => this._saveExport(blob),
        error: (error) => this._failExport(error),
      });
  }

  private _saveExport(blob: Blob): void {
    this._fileDownload.save(blob, this._fileDownload.timestampedName(EXPORT_FILENAME_PREFIX));
    this._isExporting.set(false);
    this._msgService.showSnackBar(EXPORT_STARTED_MESSAGE, { type: 'success' });
  }

  private _failExport(error: unknown): void {
    this._isExporting.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
